import { useEffect, useState } from "react";
import { Camera, Plus, Trash2, Wand2 } from "lucide-react";
import { buttonClass, fieldClass, primaryClass, WorkflowError, useWorkflowAction } from "./shared";
import { PlanEditor } from "./PlanEditor";
import { MediaThumb } from "./MediaThumb";
import { GuideSender } from "./GuideSender";

const TEMPLATE = [["写真を撮ってください。", "平面図の番号の位置に立ち、矢印の向きに撮ってください。", true]];

const newItem = (title = "", instruction = "", required = true) => ({ id: crypto.randomUUID().slice(0, 8), title, instruction, required, media: "photo", x: null, y: null, angle: -90 });

const ShotPhotos = ({ photos, onChanged }) => photos.length ? (
  <div className="flex flex-wrap gap-2">
    {photos.map((ph) => (
      <span key={ph.id} className="relative block">
        <MediaThumb item={ph} onChanged={onChanged} testId={`guide-photo-${ph.id}`} className={ph.kind === "video" ? "h-16 w-28" : "h-16 w-16"} />
        <span className={`pointer-events-none absolute bottom-0 left-0 right-0 bg-ink/80 text-center text-[9px] ${ph.status === "差戻し" ? "text-red-300" : ph.status === "承認" ? "text-green-300" : "text-amber-300"}`}>{ph.status}</span>
      </span>
    ))}
  </div>
) : <p className="text-xs text-gray-600">未撮影</p>;

export const PhotoGuidePanel = ({ data, onChanged }) => {
  const { project: p, workflow: w, survey_photos: photos = [] } = data;
  const [items, setItems] = useState(w.photo_guide || []);
  const [plan, setPlan] = useState(w.photo_plan || { kind: "none", template: "", image_id: "" });
  const [selected, setSelected] = useState(null);
  const { busy, error, run } = useWorkflowAction(p.id, onChanged);
  const processing = photos.some((ph) => ph.kind === "video" && ph.state === "processing");
  useEffect(() => {
    if (!processing) return undefined;
    const timer = setInterval(onChanged, 6000);
    return () => clearInterval(timer);
  }, [processing, onChanged]);
  const payload = { plan, items: items.map((it) => ({ ...it, title: it.title.trim() })) };
  const invalid = items.some((it) => !it.title.trim());
  const update = (i, key, value) => setItems((list) => list.map((it, j) => (j === i ? { ...it, [key]: value } : it)));
  return (
    <section data-testid="workflow-photo-guide" className="space-y-4 border-t border-line pt-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-bold text-white">現地写真の撮影指示（スタッフに表示）</h2>
          <p className="mt-1 text-xs text-gray-500">平面図に撮る位置と向きを置くと、スタッフは現地で番号を押してそのまま撮影できます。</p>
        </div>
        <button type="button" data-testid="photo-guide-template" onClick={() => setItems((list) => [...list, ...TEMPLATE.map((t) => newItem(...t))])} className={buttonClass}><Wand2 className="h-4 w-4" />標準の指示を追加</button>
      </div>
      <PlanEditor projectId={p.id} plan={plan} setPlan={setPlan} items={items} setItems={setItems} selected={selected} setSelected={setSelected} photos={photos} />
      {items.length === 0 && <p data-testid="photo-guide-empty" className="text-sm text-gray-500">撮影指示はまだありません。「標準の指示を追加」または「項目を追加」で作成してください。</p>}
      <ol className="space-y-3">
        {items.map((it, i) => (
          <li key={it.id} data-testid={`photo-guide-item-${i}`} className={`grid gap-3 border bg-panel p-3 lg:grid-cols-[2.5rem_1fr_auto] ${selected === i ? "border-accent" : "border-line"}`}>
            <button type="button" data-testid={`photo-guide-select-${i}`} onClick={() => setSelected(i)} title="平面図に配置する番号として選ぶ" className={`flex h-9 w-9 items-center justify-center rounded-full font-mono text-sm font-bold ${selected === i ? "bg-accent text-ink" : "border border-accent/60 text-accent"}`}>{i + 1}</button>
            <div className="min-w-0 space-y-2">
              <input data-testid={`photo-guide-title-${i}`} value={it.title} onChange={(e) => update(i, "title", e.target.value)} placeholder="撮る場所（例：全景）" className={fieldClass} />
              <textarea data-testid={`photo-guide-instruction-${i}`} rows={2} value={it.instruction} onChange={(e) => update(i, "instruction", e.target.value)} placeholder="撮り方（立つ位置・向き・入れる物など）" className={fieldClass} />
              <ShotPhotos onChanged={onChanged} photos={photos.filter((ph) => ph.guide_id === it.id)} />
            </div>
            <div className="flex items-start gap-3">
              <select data-testid={`photo-guide-media-${i}`} value={it.media || "photo"} onChange={(e) => update(i, "media", e.target.value)} className="border border-line bg-ink px-2 py-1 text-xs text-white"><option value="photo">写真</option><option value="video">動画（60秒まで）</option></select>
              <label className="flex items-center gap-2 text-xs text-gray-300"><input data-testid={`photo-guide-required-${i}`} type="checkbox" checked={it.required} onChange={(e) => update(i, "required", e.target.checked)} className="accent-accent" />必須</label>
              <button type="button" data-testid={`photo-guide-remove-${i}`} onClick={() => { setItems((list) => list.filter((_, j) => j !== i)); setSelected(null); }} className="p-1 text-gray-500 hover:text-red-400" aria-label="削除"><Trash2 className="h-4 w-4" /></button>
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap gap-3">
        <button type="button" data-testid="photo-guide-add" onClick={() => { setItems((list) => [...list, newItem()]); setSelected(items.length); }} className={buttonClass}><Plus className="h-4 w-4" />項目を追加</button>
        <button type="button" data-testid="photo-guide-save" disabled={busy || invalid} onClick={() => run("put", "photo-guide", payload)} className={primaryClass}><Camera className="h-4 w-4" />撮影指示を保存</button>
      </div>
      <WorkflowError error={error} id="photo-guide-error" />
      <GuideSender data={data} payload={payload} disabled={invalid || items.length === 0} onChanged={onChanged} />
    </section>
  );
};
